import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DivTooltip } from '@wg/wows-react-uikit';
import type { IReward, GameItemReward } from '~/types';
import styles from '~/Components/ProgressBar/FinalReward/FinalReward.scss';
import Wreath from '~/Components/Wreath/Wreath';
import { getShipIdFromReward } from '~/utils/rewards';
import Rays from '~/Components/Rays/Rays';
import RewardShipTooltip from '~/Components/Tooltips/RewardShipTooltip/RewardShipTooltip';
import { ship } from '~/wowsEntities';
import { useAppDispatch } from '~/hooks';
import { openShipFeaturesDialog } from '~/store/dialogs';
import { REWARD_COLORS } from '~/constants';
import { playButtonDialogClickSound } from '@wg/web2clientapi/sound';


const FinalRewardShip = React.memo(({
    finalRewardData,
    isCollected,
}: {
    finalRewardData: IReward,
    isCollected: boolean,
}) => {
    const backStyles = {
        backgroundImage: `url(${finalRewardData.flagImage})`,
    };
    const frontStyles = {
        backgroundImage: `url(${finalRewardData.image})`,
    };

    return (
        <div
            className={classnames({
                [styles.collected]: isCollected,
            })}
        >
            <div className={styles.back} style={backStyles} />
            <div
                className={classnames(styles.raysHolder, {
                    [styles.raysHolderVisible]: !!isCollected,
                })}
            >
                <Rays className={styles.rays} color={REWARD_COLORS.YELLOW} />
            </div>
            <div className={styles.front} style={frontStyles} id={'finalReward'} />
        </div>
    );
});

type PropsType = {
    rewards: Array<IReward>,
    level: number,
    reachableCount: number,
    isCollected: boolean,
    isShipPreviewAvailable: boolean,
    isFreeMainReward: boolean,
    secondaryShip?: GameItemReward | null,
    onClick: () => void,
};

const FinalReward: React.FC<PropsType> = ({
    level,
    onClick,
    rewards,
    isCollected,
    reachableCount,
    isShipPreviewAvailable,
    secondaryShip,
    isFreeMainReward,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const finalRewardData = rewards.find((reward) => reward.isMainReward);

    if (!finalRewardData) {
        return null;
    }

    const shipId = getShipIdFromReward(finalRewardData);

    const isPremiumLevel = finalRewardData.shipLevelNumber > reachableCount;

    const tooltip = (
        <RewardShipTooltip
            id={shipId as string}
            reward={finalRewardData}
            isCollected={isCollected}
            isPremium={isPremiumLevel}
            secondaryShip={secondaryShip}
            isFreeMainReward={isFreeMainReward}
        />
    );

    const openShipFeatures = (e: React.MouseEvent) => {
        playButtonDialogClickSound();
        e.stopPropagation();
        dispatch(openShipFeaturesDialog(Number(shipId)));
    };

    return (
        <DivTooltip className={styles.finalReward} tooltipBody={tooltip} onClick={onClick} fixed position={'top'}>
            <FinalRewardShip finalRewardData={finalRewardData} isCollected={isCollected}/>
            {isCollected && <div className={styles.collectedMark} />}
            <header className={styles.header}>
                {t('Главная награда')}
            </header>
            <main className={styles.main}>
                {shipId && ship(shipId, false, isShipPreviewAvailable ? openShipFeatures : undefined, true)}
            </main>
            <footer className={styles.footer}>
                <Wreath color={'#FFCC66'} />
                <span className={styles.numberReward}>{level}</span>
            </footer>
        </DivTooltip>
    );
};

export default React.memo(FinalReward);
